import { useState, useCallback, useEffect, useMemo } from 'react';
import { message } from 'antd';
import { pw9236f1Api, F9460, F9460WithRefs } from '../../../services/pw9236f1Api';

export type Mode = 'browse' | 'add' | 'edit';

export function useBonusDistForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F9460[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [refData, setRefData] = useState<F9460WithRefs | null>(null);

  // Form fields
  const [empCode, setEmpCode] = useState('');
  const [bonusType, setBonusType] = useState('');
  const [deptCode, setDeptCode] = useState('');
  const [amount, setAmount] = useState<number>(0);
  const [subsidy, setSubsidy] = useState<number>(0);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // BR-C02: Compute tax and net pay from ref data
  const taxRate = refData?.f933_22 ?? 0;
  const tax = useMemo(() => Math.round((amount + subsidy) * taxRate / 100), [amount, subsidy, taxRate]);
  const netPay = useMemo(() => amount + subsidy - tax, [amount, subsidy, tax]);

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pw9236f1Api.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Load reference data for current record (BR-C01)
  const loadRefData = useCallback(async (emp: string, bonus: string) => {
    try {
      const ref = await pw9236f1Api.get(emp, bonus);
      setRefData(ref);
    } catch {
      setRefData(null);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setEmpCode(current.f9460_01);
      setBonusType(current.f9460_02);
      setDeptCode(current.f9460_10);
      setAmount(current.f9460_21);
      setSubsidy(current.f9460_22);
      loadRefData(current.f9460_01, current.f9460_02);
    }
  }, [current, mode, loadRefData]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setEmpCode('');
    setBonusType('');
    setDeptCode('');
    setAmount(0);
    setSubsidy(0);
    setRefData(null);
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setEmpCode(current.f9460_01);
    setBonusType(current.f9460_02);
    setDeptCode(current.f9460_10);
    setAmount(current.f9460_21);
    setSubsidy(current.f9460_22);
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      setEmpCode(current.f9460_01);
      setBonusType(current.f9460_02);
      setDeptCode(current.f9460_10);
      setAmount(current.f9460_21);
      setSubsidy(current.f9460_22);
    }
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await pw9236f1Api.create({
          f9460_01: empCode,
          f9460_02: bonusType,
          f9460_10: deptCode,
          f9460_21: amount,
          f9460_22: subsidy,
        });
        message.success('新增成功');
      } else {
        await pw9236f1Api.update(current!.f9460_01, current!.f9460_02, {
          f9460_10: deptCode,
          f9460_21: amount,
          f9460_22: subsidy,
        });
        message.success('修改成功');
      }
      const targetEmp = mode === 'add' ? empCode : current!.f9460_01;
      const targetBonus = mode === 'add' ? bonusType : current!.f9460_02;
      const data = await loadRecords();
      const idx = data.findIndex(r => r.f9460_01 === targetEmp && r.f9460_02 === targetBonus);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await pw9236f1Api.delete(current.f9460_01, current.f9460_02);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
        setRefData(null);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search (BR-F02: by empCode + bonusType)
  const quickSearch = (searchEmp: string, searchBonus: string) => {
    if (isEditing) return;
    const idx = records.findIndex(
      r => r.f9460_01 >= searchEmp && (searchBonus === '' || r.f9460_02 >= searchBonus)
    );
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx, refData,
    empCode, setEmpCode,
    bonusType, setBonusType,
    deptCode, setDeptCode,
    amount, setAmount,
    subsidy, setSubsidy,
    tax, netPay,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    loadRecords,
  };
}
